// Ventura Voice Recognition Hook
// Supports both Web Speech API (online) and Sherpa-ONNX WASM (offline)

import { useState, useEffect, useCallback, useRef } from 'react';
import type { VoiceState, VoiceIntent } from '@/types';
import { VoiceService, SherpaVoiceService, type VoiceConfig, type SherpaVoiceConfig } from '@/services/voice';
import { useSettingsStore } from '@/store/settingsStore';

interface UseVenturaVoiceOptions {
  onIntent?: (intent: VoiceIntent) => void;
  onError?: (error: Error) => void;
}

interface UseVenturaVoiceResult {
  state: VoiceState;
  transcript: string;
  isListening: boolean;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
  activatePushToTalk: () => void;
  setDeviceId: (deviceId: string) => Promise<void>;
  isSupported: boolean;
  isInitialized: boolean;
  isOffline: boolean;
  isLoading: boolean;
  loadProgress: string;
}

// Common interface for both voice services
interface VoiceServiceInterface {
  initialize: () => boolean | Promise<boolean>;
  startListening: () => void | Promise<void>;
  stopListening: () => void;
  activatePushToTalk: () => void;
  setFollowUpDuration: (durationMs: number) => void;
}

export function useVenturaVoice(options: UseVenturaVoiceOptions = {}): UseVenturaVoiceResult {
  const { onIntent, onError } = options;
  const voiceMode = useSettingsStore((s) => s.voiceMode);
  const voiceFollowUpDuration = useSettingsStore((s) => s.voiceFollowUpDuration);
  const offlineVoice = useSettingsStore((s) => s.offlineVoice);

  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState('');

  const voiceServiceRef = useRef<VoiceServiceInterface | null>(null);
  const initializingRef = useRef(false);
  const voiceModeRef = useRef(voiceMode);
  const voiceFollowUpDurationRef = useRef(voiceFollowUpDuration);
  const offlineVoiceRef = useRef(offlineVoice);
  const hasInitializedModeRef = useRef(false);

  // Store callbacks in refs to avoid re-initialization
  const onIntentRef = useRef(onIntent);
  const onErrorRef = useRef(onError);
  onIntentRef.current = onIntent;
  onErrorRef.current = onError;

  // Create service based on mode
  const createService = useCallback((useOffline: boolean): VoiceServiceInterface => {
    const commonConfig = {
      continuous: true,
      followUpDurationMs: voiceFollowUpDurationRef.current * 1000,
      onStateChange: setState,
      onTranscript: (text: string, isFinal: boolean) => {
        setTranscript(text);
        if (isFinal) {
          setTimeout(() => setTranscript(''), 3000);
        }
      },
      onIntent: (intent: VoiceIntent) => {
        onIntentRef.current?.(intent);
      },
      onError: (error: Error) => {
        onErrorRef.current?.(error);
      },
    };

    if (useOffline) {
      const sherpaConfig: SherpaVoiceConfig = {
        ...commonConfig,
        onLoadProgress: (progress: number, message: string) => {
          setLoadProgress(message);
          if (progress >= 100) {
            setIsLoading(false);
          }
        },
      };
      return new SherpaVoiceService(sherpaConfig);
    } else {
      const webConfig: VoiceConfig = commonConfig;
      return new VoiceService(webConfig);
    }
  }, []);

  // Initialize on mount or when offline mode changes
  useEffect(() => {
    const initVoice = async () => {
      // Skip if already initializing
      if (initializingRef.current) return;

      // Check if we need to switch services
      const needsNewService = !voiceServiceRef.current || offlineVoiceRef.current !== offlineVoice;

      if (!needsNewService && voiceServiceRef.current) return;

      // Clean up existing service
      if (voiceServiceRef.current) {
        voiceServiceRef.current.stopListening();
        voiceServiceRef.current = null;
        setIsInitialized(false);
      }

      initializingRef.current = true;
      offlineVoiceRef.current = offlineVoice;

      // Check support
      const hasWebSpeech = typeof window !== 'undefined' &&
        ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

      // Sherpa-ONNX is always supported (WASM works everywhere)
      const hasSupport = offlineVoice ? true : hasWebSpeech;
      setIsSupported(hasSupport);

      if (!hasSupport) {
        console.log('No speech recognition support');
        initializingRef.current = false;
        return;
      }

      // Show loading for offline mode
      if (offlineVoice) {
        setIsLoading(true);
        setLoadProgress('Initializing offline voice...');
      }

      // Create the appropriate service
      voiceServiceRef.current = createService(offlineVoice);

      try {
        const success = await voiceServiceRef.current.initialize();
        console.log(`${offlineVoice ? 'Sherpa-ONNX' : 'Web Speech'} voice service initialized:`, success);
        setIsInitialized(success);

        // Show "READY!" briefly for offline mode before clearing loading state
        if (success && offlineVoice) {
          setLoadProgress('READY!');
          setTimeout(() => {
            setIsLoading(false);
            setLoadProgress('');
          }, 1500);
        } else {
          setIsLoading(false);
        }

        // Auto-start in full voice mode
        if (success) {
          setTimeout(() => {
            const currentMode = voiceModeRef.current;
            console.log('Checking voice mode after initialization:', currentMode);

            if (currentMode === 'full') {
              console.log('Auto-starting voice listening (full voice mode)...');
              voiceServiceRef.current?.startListening();
            } else {
              console.log('PTT mode - waiting for manual activation');
            }
          }, offlineVoice ? 1600 : 100); // Wait for READY flash in offline mode
        }
      } catch (error) {
        console.error('Failed to initialize voice service:', error);
        setIsInitialized(false);
        setIsLoading(false);
        setLoadProgress('');
      }

      initializingRef.current = false;
    };

    initVoice();

    return () => {
      voiceServiceRef.current?.stopListening();
      voiceServiceRef.current = null;
      initializingRef.current = false;
    };
  }, [offlineVoice, createService]);

  // Respond to voice mode changes (only after initial setup)
  useEffect(() => {
    voiceModeRef.current = voiceMode;

    // Skip until service is initialized
    if (!voiceServiceRef.current || !isInitialized) return;

    // Skip the first run - initial mode is handled in the mount effect
    if (!hasInitializedModeRef.current) {
      hasInitializedModeRef.current = true;
      return;
    }

    // Only respond to actual user-initiated mode changes
    if (voiceMode === 'full') {
      console.log('Switching to full voice mode - starting listening');
      voiceServiceRef.current.startListening();
    } else {
      console.log('Switching to PTT mode - stopping listening');
      voiceServiceRef.current.stopListening();
    }
  }, [voiceMode, isInitialized]);

  // Respond to follow-up duration changes
  useEffect(() => {
    voiceFollowUpDurationRef.current = voiceFollowUpDuration;

    // Update the voice service if it exists
    if (voiceServiceRef.current) {
      voiceServiceRef.current.setFollowUpDuration(voiceFollowUpDuration * 1000);
    }
  }, [voiceFollowUpDuration]);

  const startListening = useCallback(async () => {
    console.log('useVenturaVoice.startListening called');
    voiceServiceRef.current?.startListening();
  }, []);

  const stopListening = useCallback(async () => {
    console.log('useVenturaVoice.stopListening called');
    setState('idle');
    voiceServiceRef.current?.stopListening();
  }, []);

  const activatePushToTalk = useCallback(() => {
    console.log('useVenturaVoice.activatePushToTalk called');
    voiceServiceRef.current?.activatePushToTalk();
  }, []);

  const setDeviceId = useCallback(async (newDeviceId: string) => {
    console.log('setDeviceId called:', newDeviceId, 'voiceMode:', voiceModeRef.current);
    if (voiceServiceRef.current) {
      voiceServiceRef.current.stopListening();
      if (voiceModeRef.current === 'full') {
        voiceServiceRef.current.startListening();
      }
    }
  }, []);

  return {
    state,
    transcript,
    isListening: state === 'listening-wake' || state === 'listening-command',
    startListening,
    stopListening,
    activatePushToTalk,
    setDeviceId,
    isSupported,
    isInitialized,
    isOffline: offlineVoice,
    isLoading,
    loadProgress,
  };
}
